import React, { useContext, useEffect, useState } from "react";
import { useTheme } from "./ThemeContext";
import { AuthContext } from "./AuthContext";
import { Link, useNavigate } from "react-router-dom";
import MobileHeader from "./MobileHeader";
import Spinner from "./Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faIdBadge,
  faCreditCard,
  faMapMarkerAlt,
  faClipboardList,
  faFileAlt,
  faCogs,
  faUsers,
  faBullhorn,
  faMoneyBillAlt,
  faChartBar,
  faEnvelopeOpenText,
  faHandshake,
  faCode,
  faPercent,
} from "@fortawesome/free-solid-svg-icons";

const AdminSettings = () => {
  const { currentUser } = useContext(AuthContext);
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!currentUser) {
      navigate("/");
      return;
    }
  }, [currentUser, navigate]);

  if (!currentUser) return null;

  // Define sections and their settings items.
  const sections = [
    {
      title: "Account Management",
      items: [
        {
          title: "Accounts",
          description: "Manage Accounts",
          url: "/cardgroups",
          icon: faIdBadge,
        },
        {
          title: "Cards",
          description: "Manage Cards",
          url: "/card_admin",
          icon: faCreditCard,
        },
        {
          title: "Discount Groups",
          description: "Add, Edit, or Remove Discount Groups",
          url: "/discount-groups",
          icon: faCogs,
          condition: currentUser.isAdmin || currentUser.isContractAdmin,
        },
        {
          title: "Locations",
          description: "Manage and View Locations",
          url: "/locationdata",
          icon: faMapMarkerAlt,
          condition: currentUser.isAdmin,
        },
        {
          title: "Carrier Pricing Quotes",
          description: "Generate branded pricing quotes for carriers",
          url: "/admin-locations",
          icon: faFileAlt,
          condition: currentUser.isAdmin,
        },
      ],
    },
    {
      title: "Orders & Contracts",
      items: [
        {
          title: "Card Orders",
          description: "Manage Card Orders",
          url: "/cardorders",
          icon: faClipboardList,
          condition: currentUser.isAdmin,
        },
        {
          title: "Contracts",
          description: "View your EFS Contracts",
          url: "/admin_efscontracts",
          icon: faFileAlt,
          condition: currentUser.isAdmin,
        },
        {
          title: "Policies",
          description: "Manage Policies",
          url: "/policies",
          icon: faCogs,
          condition: currentUser.isAdmin || currentUser.isContractAdmin,
        },
      ],
    },
    {
      title: "Business Management",
      items: [
        {
          title: "CRM",
          description: "Manage Leads and Customers",
          url: "/crm",
          icon: faUsers,
          condition: currentUser.isAdmin,
        },       
        {
          title: "Marketing",
          description: "Create & Edit Marketing Campaigns",
          url: "/marketing",
          icon: faBullhorn,
          condition: currentUser.isAdmin || currentUser.isContractAdmin,
        },
        {
          title: "TOS Manager",
          description: "Manage User TOS Agreements",
          url: "/tosmanager",
          icon: faEnvelopeOpenText,
          condition: currentUser.isAdmin || currentUser.isContractAdmin,
        },
      ],
    },
    {
      title: "Financial & Transactions",
      items: [
        {
          title: "Financial Transactions",
          description: "Manage Financial Transactions",
          url: "/adminfinancialtransactions",
          icon: faMoneyBillAlt,
        },
        {
          title: "FBT Transactions",
          description: "FBT Payments and Transactions",
          url: "/fbttransactions",
          icon: faMoneyBillAlt,
          condition: currentUser.HasFBTAccess,
        },
        {
          title: "Fuel Transactions",
          description: "View EFS Fuel Transactions",
          url: "/admin_transactions",
          icon: faFileAlt,
          condition: currentUser.isAdmin || currentUser.isContractAdmin,
        },
        {
          title: "Reports",
          description: "Generate Custom Reports",
          url: "/adminreports",
          icon: faChartBar,
        },
        {
          title: "Usage / Stats",
          description: "Billing, Usage, and Stats",
          url: "/usagestats",
          icon: faChartBar,
          condition: currentUser.isAdmin,
        },
      ],
    },
    {
      title: "Affiliate Program",
      items: [
        {
          title: "Affiliate Management",
          description: "Manage Affiliate Accounts & Applications",
          url: "/admin/affiliates",
          icon: faHandshake,
          condition: currentUser.isAdmin,
        },
        {
          title: "Referral Codes",
          description: "Manage Affiliate Referral Codes",
          url: "/admin/affiliate-codes",
          icon: faCode,
          condition: currentUser.isAdmin,
        },
        {
          title: "Commission Tracking",
          description: "View & Manage Affiliate Commissions",
          url: "/admin/affiliate-commissions",
          icon: faPercent,
          condition: currentUser.isAdmin,
        },
      ],
    },
    {
      title: "User Management",
      items: [
        {
          title: "Users",
          description: "View User Details",
          url: "/user-table",
          icon: faUser,
          condition: currentUser.isAdmin || currentUser.isContractAdmin,
        },
      ],
    },
  ];

  return (
    <>
      <MobileHeader theme={theme} />
      <div className="max-w-6xl mx-auto p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-8">Admin Settings</h1>
        {isLoading && <Spinner />}
        {sections.map((section, index) => (
          <div key={index} className="mb-10">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">
              {section.title}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {section.items.map((item, i) => {
                // If a condition exists and is false, skip rendering this item.
                if (item.condition === false) return null;
                return (
                  <Link
                    key={i}
                    to={item.url}
                    className="flex items-center p-4 bg-white rounded-lg shadow hover:shadow-lg transition-shadow duration-200"
                  >
                    <div className="mr-4">
                      <FontAwesomeIcon icon={item.icon} className="themedIcon3xl" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {item.title}
                      </h3>
                      <p className="text-gray-500">{item.description}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};

export default AdminSettings;



// import React, { useContext, useEffect, useState } from "react";
// import { useTheme } from "./ThemeContext";
// import { AuthContext } from "./AuthContext";
// import AdminSettingsCard from "./AdminSettingsCard";
// import { useNavigate } from "react-router-dom";
// import Spinner from "./Spinner";
// import MobileHeader from './MobileHeader';

// const AdminSettings = () => {
//   const { currentUser, logout } = useContext(AuthContext);
//   const { theme } = useTheme();
//   const navigate = useNavigate();
//   const [isLoading, setIsLoading] = useState(false);

//   useEffect(() => {
//     if (!currentUser) {
//       navigate("/");
//       return;
//     }
//   }, [currentUser, navigate]);

//   if (!currentUser) {
//     return null;
//   }

//   // const handleLinkStripeDashboard = () => {
//   //   setIsLoading(true);
//   //   fetch(
//   //     "https://fuellink-stripe.azurewebsites.net/api/createNewConnectedAccount",
//   //     {
//   //       method: "POST",
//   //       headers: {
//   //         "Content-Type": "application/json",
//   //       },
//   //       body: JSON.stringify({ userId: currentUser.userId }),
//   //     }
//   //   )
//   //     .then((response) => response.json())
//   //     .then((data) => {
//   //       if (data && data.url) {
//   //         window.location.href = data.url;
//   //       }
//   //     })
//   //     .finally(() => {
//   //       setIsLoading(false);
//   //     });
//   // };

//   // const stripeSectionStyle = {
//   //   background: "linear-gradient(135deg, #6266FF, #00A3FF)",
//   //   boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
//   //   padding: "20px",
//   //   borderRadius: "8px",
//   //   color: "white",
//   //   textAlign: "center",
//   //   marginBottom: "20px",
//   // };

//   return (
//     <>
//       <MobileHeader theme={theme} />
//       <div className="flex-1 p-6 overflow-x-hidden mobile-container">
//         {isLoading && <Spinner />}
//         {/* {currentUser.isAdmin && (
//           <div style={stripeSectionStyle} className={"ml-2 mr-4"}>
//             <h2>View Your Stripe Dashboard</h2>
//             <button
//               onClick={handleLinkStripeDashboard}
//               className="stripe-button"
//             >
//               <FontAwesomeIcon icon={faExternalLinkAlt} className="mr-2" />
//               View Stripe Dashboard
//             </button>
//           </div>
//         )} */}
//         <div className="flex flex-wrap justify-start items-stretch">
//           <AdminSettingsCard
//             title="Accounts"
//             description="Manage Accounts"
//             theme={theme}
//             url="/cardgroups"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           <AdminSettingsCard
//             title="Cards"
//             description="Manage Cards"
//             theme={theme}
//             url="/card_admin"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           { currentUser.isAdmin  && (
//           <AdminSettingsCard
//             title="Card Orders"
//             description="Manage Card Orders"
//             theme={theme}
//             url="/cardorders"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { currentUser.isAdmin  && (
//           <AdminSettingsCard
//             title="Contracts"
//             description="View your EFS Contracts"
//             theme={theme}
//             url="/admin_efscontracts"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { currentUser.isAdmin && (
//           <AdminSettingsCard
//             title="CRM"
//             description="Manage Leads and Customers"
//             theme={theme}
//             url="/crm"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { (currentUser.isAdmin || currentUser.isContractAdmin) && (
//           <AdminSettingsCard
//             title="Discount Groups"
//             description="Add, Edit, or Remove Discount Groups"
//             theme={theme}
//             url="/discount-groups"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { currentUser.HasFBTAccess && (
//           <AdminSettingsCard
//             title="FBT Transactions"
//             description="FBT Payments and Transactions"
//             theme={theme}
//             url="/fbttransactions"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           <AdminSettingsCard
//             title="Financial Transactions"
//             description="Create, Edit, and Manage Financial Transactions"
//             theme={theme}
//             url="/adminfinancialtransactions"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           {/* <AdminSettingsCard
//             title="Inventory"
//             description="Manage Products and Inventory"
//             theme={theme}
//             url="/inventory"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           /> */}
//           {/* <AdminSettingsCard
//             title="Invoices"
//             description="Manage Invoices"
//             theme={theme}
//             url="/invoices"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           /> */}
//           {/* <AdminSettingsCard
//             title="Orders"
//             description="Manage Orders"
//             theme={theme}
//             url="/orders"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           /> */}
//           {/* {currentUser.isAdmin && (
//             <AdminSettingsCard
//               title="Invite A Contract User"
//               description="Create a New Contract Admin Account."
//               theme={theme}
//               url="/invite_contract_user"
//               className="w-full sm:w-1/2 md:w-1/3 p-2"
//             />
//           )} */}
//           {/* <AdminSettingsCard 
//             title="Invite A Policy User" 
//             description="Create a New User Account." 
//             theme={theme} 
//             url="/invite-user-form"
//           /> */}
//           {/* <AdminSettingsCard
//             title="Invoices"
//             description="Manage Invoices"
//             theme={theme}
//             url="/admin_accounting"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           /> */}
//           { currentUser.isAdmin && (
//           <AdminSettingsCard
//             title="Locations"
//             description="Manage and View Locations"
//             theme={theme}
//             url="/locationdata"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { (currentUser.isAdmin || currentUser.isContractAdmin) && (
//           <AdminSettingsCard
//             title="Marketing"
//             description="Create & Edit Marketing Campaigns"
//             theme={theme}
//             url="/marketing"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { (currentUser.isAdmin || currentUser.isContractAdmin) && (
//           <AdminSettingsCard
//             title="Policies"
//             description="Manage policies"
//             theme={theme}
//             url="/policies"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           <AdminSettingsCard
//             title="Reports"
//             description="Generate Custom Reports"
//             theme={theme}
//             url="/adminreports"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           { (currentUser.isAdmin || currentUser.isContractAdmin) && (
//           <AdminSettingsCard
//             title="TOS Manager"
//             description="Manage your user TOS agreements"
//             theme={theme}
//             url="/tosmanager"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { (currentUser.isAdmin || currentUser.isContractAdmin) && (
//           <AdminSettingsCard
//             title="Transactions"
//             description="View Your EFS Transactions"
//             theme={theme}
//             url="/admin_transactions"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { currentUser.isAdmin && (
//           <AdminSettingsCard
//             title="Usage / Stats"
//             description="Billing, Usage, and Stats"
//             theme={theme}
//             url="/usagestats"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}
//           { (currentUser.isAdmin || currentUser.isContractAdmin) && (
//           <AdminSettingsCard
//             title="Users"
//             description="View User Details"
//             theme={theme}
//             url="/user-table"
//             className="w-full sm:w-1/2 md:w-1/3 p-2"
//           />
//           )}          
//         </div>
//       </div>
//     </>
//   );
// };

// export default AdminSettings;
